/**
 * Who a transaction belongs to.
 *
 * A transaction belongs to a **card**, and the card owns the person
 * relationship. `Transaction.personId` is a mirror of `Card.personId`, kept as
 * its own field for two reasons: it feeds the dedupe key (see `identity.ts`), so
 * dropping it would change every transaction id, and the person+date compound
 * index is what makes the filter bar fast.
 *
 * A mirror drifts. Reassigning a card's owner used to leave its rows behind,
 * and deleting a person used to leave rows pointing at an id that no longer
 * existed — in both cases every per-person total is silently wrong, because the
 * rows still aggregate under a person nobody can select.
 *
 * This module is the one place that decides such a disagreement, and the rule is
 * always the same: **the card wins.** Nothing here guesses. A row whose card is
 * gone has no owner to inherit and is reported as an orphan rather than assigned
 * to whichever card came to hand.
 */

import type { Card, Person, Transaction } from './types.ts';

/** A row that names a card and mirrors that card's owner. */
export interface CardScoped {
  cardId: string;
  personId: string;
}

export interface PersonDrift<T> {
  row: T;
  /** The personId the row currently claims. */
  from: string;
  /** Its card's personId, which is authoritative. */
  to: string;
}

/**
 * Rows whose `personId` disagrees with their card's, in input order.
 *
 * Rows whose card is missing are skipped, not repaired: there is no owner to
 * copy, and picking one would merge two people's history — see
 * `findOrphanedRows`.
 */
export function findPersonDrift<T extends CardScoped>(
  rows: readonly T[],
  cards: readonly Card[],
): PersonDrift<T>[] {
  const ownerOf = new Map(cards.map((card) => [card.id, card.personId]));
  const drift: PersonDrift<T>[] = [];

  for (const row of rows) {
    const to = ownerOf.get(row.cardId);
    if (to === undefined || to === row.personId) continue;
    drift.push({ row, from: row.personId, to });
  }

  return drift;
}

/**
 * Rows whose card no longer exists.
 *
 * These cannot be repaired automatically — the card that knew the owner is
 * gone. A caller should surface them and let the reader decide.
 */
export function findOrphanedRows<T extends CardScoped>(
  rows: readonly T[],
  cards: readonly Card[],
): T[] {
  const cardIds = new Set(cards.map((card) => card.id));
  return rows.filter((row) => !cardIds.has(row.cardId));
}

/**
 * Cards whose owner no longer exists.
 *
 * The root cause of drift rather than a symptom: while a card points at a
 * deleted person, every row that correctly mirrors it is also unselectable. Only
 * a human can say who the card belongs to now.
 */
export function findDanglingCards(
  cards: readonly Card[],
  people: readonly Person[],
): Card[] {
  const personIds = new Set(people.map((person) => person.id));
  return cards.filter((card) => !personIds.has(card.personId));
}

/**
 * Transactions still pointing at a person, whether or not that person exists.
 * Used to decide whether deleting a person would strand history.
 */
export function countRowsForPerson(
  transactions: readonly Transaction[],
  personId: string,
): number {
  return transactions.reduce((count, tx) => (tx.personId === personId ? count + 1 : count), 0);
}
